import React, { useCallback, useMemo } from 'react';
import { SelectPicker, Stack } from 'rsuite';
import { CalculatedLicense } from 'src/types/tax';

export interface FilterOption {
  name: string;
  value?: string;
}

export interface TaxFilterItem {
  options: FilterOption[];
  selectedValue?: string;
  onSelect: (value?: string) => void;
}
interface TaxFiltersProps {
  data: Array<CalculatedLicense>;
  selectedValue?: [string, string];
  onSelectedValue: (value: [string, string]) => void;
}

export const TaxFilters: React.FC<TaxFiltersProps> = ({
  data,
  selectedValue,
  onSelectedValue,
}) => {
  const periodOptions: FilterOption[] = useMemo((): FilterOption[] => {
    const allOption: FilterOption = { name: 'All Periods', value: undefined };
    const periodOpts = data.map((period) => ({
      name: period.label,
      value: `${period.startDate}_${period.endDate}`,
    }));
    return [allOption, ...periodOpts];
  }, [data]);

  const licenseOptions: FilterOption[] = useMemo((): FilterOption[] => {
    const allOption = { name: 'All Licenses', value: undefined };

    const licenseOpts = data
      .flatMap((period) => period.licenses)
      .reduce((prev: Array<FilterOption>, license) => {
        if (!prev.some((opt) => opt.value === license.licenseNumber)) {
          prev.push({
            name: `${license.address} (${license.licenseNumber})`,
            value: license.licenseNumber || undefined,
          });
        }
        return prev;
      }, []);

    return [allOption, ...licenseOpts];
  }, [data]);

  const setSelectedPeriodFilter = useCallback(
    (value?: string) => {
      onSelectedValue?.([value || '', selectedValue?.[1] || '']);
    },
    [selectedValue, onSelectedValue]
  );

  const setSelectedLicenseFilter = useCallback(
    (value?: string) => {
      onSelectedValue?.([selectedValue?.[0] || '', value || '']);
    },
    [selectedValue, onSelectedValue]
  );

  const taxFiltersData = useMemo(
    (): TaxFilterItem[] => [
      {
        options: periodOptions,
        selectedValue: selectedValue?.[0],
        onSelect: setSelectedPeriodFilter,
      },
      {
        options: licenseOptions,
        selectedValue: selectedValue?.[1],
        onSelect: setSelectedLicenseFilter,
      },
    ],
    [
      periodOptions,
      licenseOptions,
      selectedValue,
      setSelectedPeriodFilter,
      setSelectedLicenseFilter,
    ]
  );

  return (
    <Stack style={{ display: 'flex', gap: '16px' }}>
      {taxFiltersData?.map((item, index) => (
        <Stack key={index}>
          <SelectPicker
            labelKey="name"
            valueKey="value"
            data={item?.options}
            searchable={false}
            value={item?.selectedValue}
            onSelect={(value) => item?.onSelect(value)}
            menuAutoWidth={false}
            menuMaxHeight={240}
            cleanable={false}
            className="tax-filter-picker"
            style={{ maxWidth: 200 }}
          />
        </Stack>
      ))}
    </Stack>
  );
};
